import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Megaphone, ImagePlus, Images } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { title: "", author: "", date: today(), body: "", image_path: "" };

export default function PressAdmin() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl">Media & Press</h1>
        <p className="text-muted-foreground">Manage official statements and the public photo gallery.</p>
      </div>
      <Tabs defaultValue="press">
        <TabsList className="rounded-full h-auto p-1 bg-muted mb-6">
          <TabsTrigger value="press" data-testid="tab-press" className="rounded-full py-2 px-5 data-[state=active]:bg-primary data-[state=active]:text-white">Press Releases</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-full py-2 px-5 data-[state=active]:bg-primary data-[state=active]:text-white">Photo Gallery</TabsTrigger>
        </TabsList>
        <TabsContent value="press"><PressManager /></TabsContent>
        <TabsContent value="gallery"><GalleryManager /></TabsContent>
      </Tabs>
    </div>
  );
}

function useMediaUpload() {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.image_path;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setUploading(false); }
  };
  return { uploading, upload };
}

function PressManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [preview, setPreview] = useState("");
  const { uploading, upload } = useMediaUpload();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(() => { setLoading(true); api.get("/admin/press").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setF(empty); setPreview(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setF({ title: p.title, author: p.author, date: (p.date || "").slice(0, 10), body: p.body, image_path: p.image_path || "" }); setPreview(p.image_path ? `${API}/files/${p.image_path}` : ""); setOpen(true); };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPreview(URL.createObjectURL(file));
    const path = await upload(file);
    if (path) { set("image_path", path); toast.success("Image uploaded"); } else setPreview("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, image_path: f.image_path || undefined };
      if (editing) await api.patch(`/admin/press/${editing.id}`, payload);
      else await api.post("/admin/press", payload);
      toast.success(editing ? "Statement updated" : "Statement published");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/press/${id}`); toast.success("Statement removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const valid = f.title && f.author && f.date && f.body && !uploading;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-press-btn" onClick={openNew} className="rounded-full bg-primary font-bold gap-2"><Plus className="w-4 h-4" /> New Statement</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit Statement" : "New Press Statement"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="mb-1.5 block text-sm font-semibold">Title</Label><Input data-testid="press-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Statement headline" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-sm font-semibold">Author / Source</Label><Input data-testid="press-author" value={f.author} onChange={(e) => set("author", e.target.value)} placeholder="e.g. Chairman" /></div>
                <div><Label className="mb-1.5 block text-sm font-semibold">Date</Label><Input data-testid="press-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
              </div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Statement</Label><Textarea data-testid="press-body" value={f.body} onChange={(e) => set("body", e.target.value)} rows={6} placeholder="Full official statement..." /></div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold">Image (optional)</Label>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-5 cursor-pointer hover:border-primary transition-colors text-sm text-muted-foreground">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}{uploading ? "Uploading..." : "Choose image"}
                  <input data-testid="press-image" type="file" accept="image/*" className="hidden" onChange={onFile} />
                </label>
                {preview && <img src={preview} alt="preview" className="mt-2 rounded-xl w-full aspect-video object-cover" />}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="press-save" onClick={save} disabled={!valid || saving} className="bg-primary gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />}{editing ? "Save" : "Publish"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        : items.length === 0 ? <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center text-muted-foreground"><Megaphone className="w-8 h-8 mx-auto mb-2 opacity-40" />No statements yet.</div>
        : (
          <div className="space-y-3">
            {items.map((p) => (
              <div key={p.id} data-testid={`press-item-${p.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-start gap-4">
                {p.image_path && <img src={`${API}/files/${p.image_path}`} alt="" className="w-24 h-16 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading font-bold text-lg">{p.title}</h3>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.author} · {(p.date || "").slice(0, 10)}</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2">{p.body}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button data-testid={`press-edit-${p.id}`} size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button data-testid={`press-delete-${p.id}`} size="icon" variant="ghost" className="text-accent hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete "{p.title}"?</AlertDialogTitle><AlertDialogDescription>This statement will be removed from the public Media & Press page.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} className="bg-accent">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState("");
  const { uploading, upload } = useMediaUpload();

  const load = useCallback(() => { setLoading(true); api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const path = await upload(file);
    if (!path) return;
    try {
      await api.post("/admin/gallery", { image_path: path, caption: caption || undefined });
      toast.success("Photo added to gallery"); setCaption(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const del = async (id) => {
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <Label className="mb-1.5 block text-sm font-semibold">Caption (optional)</Label>
            <Input data-testid="gallery-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Keffi ward rally, June 2026" />
          </div>
          <label className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white font-bold px-5 py-2.5 text-sm cursor-pointer hover:bg-primary/90 transition-colors">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}{uploading ? "Uploading..." : "Upload Photo"}
            <input data-testid="gallery-upload" type="file" accept="image/*" className="hidden" onChange={onFile} />
          </label>
        </div>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        : items.length === 0 ? <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center text-muted-foreground"><Images className="w-8 h-8 mx-auto mb-2 opacity-40" />No photos yet.</div>
        : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {items.map((g) => (
              <div key={g.id} data-testid={`gallery-admin-${g.id}`} className="relative aspect-square rounded-2xl overflow-hidden shadow-sm group">
                <img src={g.image_url || `${API}/files/${g.image_path}`} alt={g.caption || "Gallery"} className="w-full h-full object-cover" />
                {g.caption && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-2">{g.caption}</div>}
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button data-testid={`gallery-delete-${g.id}`} size="icon" className="absolute top-2 right-2 h-8 w-8 bg-white/90 text-accent hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remove this photo?</AlertDialogTitle><AlertDialogDescription>It will be removed from the public gallery.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(g.id)} className="bg-accent">Remove</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
